import { SolitaireGame } from './SolitaireGame';
import { Card } from 'components/Card';
import { ACES_VALUE, KING_VALUE } from 'constants/cards';
import { GameEvent } from './events/gameEvent';
import { EventName } from './events/eventsName';
import { AutocompleteButton } from './gameUI/AutocompleteButton';
import { getLastItem } from './helpers/array';
import { Store } from './store/gameStore';
import { GameName } from 'constants/difficulty';

export class AutocompleteWorker {
    autocompleteButton: AutocompleteButton;
    game: SolitaireGame;

    constructor(game: SolitaireGame) {
        this.game = game;
        this.makeMove = this.makeMove.bind(this);
        this.moveCard = this.moveCard.bind(this);
        this.autocompleteButton = new AutocompleteButton();
    }
    makeMove() {
        let moveCard = null as null | Card;

        switch (Store.name) {
            case GameName.KLONDIKE:
                moveCard = this.makeMoveKlondike();
                break;
            case GameName.SPIDER:
                moveCard = this.makeMoveSpider();
                break;
            case GameName.FREECELL:
                moveCard = this.makeMoveFreeCell();
                break;
            case GameName.PYRAMID:
                moveCard = this.makeMovePyramid();
                break;
        }

        if (moveCard) this.moveCard(moveCard);
        else {
            GameEvent.emit(EventName.AUTOCOMPLETE_COMPLETE);
            return false;
        }
        return true;
    }
    makeMoveFreeCell(): Card | null {
        for (let i = 0; i < this.game.freeCells.length; i++) {
            const freeCell = this.game.freeCells[i];
            if (freeCell.lastCard) {
                const cellCard = freeCell.lastCard;
                for (let j = 0; j < this.game.foundations.length; j++) {
                    const foundation = this.game.foundations[j];
                    const lastFoundationCard = foundation.lastCard;
                    if (lastFoundationCard && lastFoundationCard.canDropInFoundation(cellCard)) {
                        return cellCard;
                    }
                    if (cellCard.value === ACES_VALUE) return cellCard;
                }
            }
        }
        for (let col = 0; col < this.game.columns.length; col++) {
            const column = this.game.columns[col];
            const card = column.lastCard;
            if (!card) continue;
            for (let j = 0; j < this.game.foundations.length; j++) {
                const foundation = this.game.foundations[j];
                const lastFoundationCard = foundation.lastCard;
                if (lastFoundationCard && lastFoundationCard.canDropInFoundation(card)) {
                    return card;
                }
                if (card.value === ACES_VALUE) return card;
            }
        }
        return null;
    }
    makeMoveSpider() {
        let moveCard = null as null | Card;
        for (let col = 0; col < this.game.columns.length; col++) {
            const column = this.game.columns[col];
            const card = column.cards[0];
            if (card && card.value === KING_VALUE && column.length === 13) {
                moveCard = card;
                break;
            }
            if (card && card.value !== KING_VALUE) {
                moveCard = card;
                break;
            }
        }

        return moveCard;
    }
    makeMoveKlondike() {
        let moveCard = null as null | Card;
        for (let col = 0; col < this.game.columns.length; col++) {
            const column = this.game.columns[col];
            const card = column.lastCard;

            if (card) {
                this.game.foundations.forEach((f) => {
                    const lastFoundationCard = f.lastCard;
                    if (lastFoundationCard) {
                        if (lastFoundationCard.canDropInFoundation(card)) {
                            moveCard = card;
                            col = 9999;
                        }
                    } else {
                        if (card.value === 1) {
                            moveCard = card;
                            col = 9999;
                        }
                    }
                });
            }
        }

        if (!moveCard) {
            const card = this.game.wastePile?.lastCard;
            if (card) {
                this.game.foundations.forEach((f) => {
                    const lastFoundationCard = f.lastCard;
                    if (lastFoundationCard) {
                        if (lastFoundationCard.canDropInFoundation(card)) {
                            moveCard = card;
                        }
                    } else {
                        if (card.value === 1) moveCard = card;
                    }
                });
            }
        }
        return moveCard;
    }
    makeMovePyramid() {
        let moveCard = null as null | Card;
        for (let col = 0; col < this.game.columns.length; col++) {
            const column = this.game.columns[col];
            const card = column.lastCard;

            if (card) {
                this.game.foundations.forEach((f) => {
                    const lastFoundationCard = f.lastCard;
                    if (lastFoundationCard) {
                        if (lastFoundationCard.canDropInFoundation(card)) {
                            moveCard = card;
                            col = 9999;
                        }
                    } else {
                        if (card.value === 1) {
                            moveCard = card;
                            col = 9999;
                        }
                    }
                });
            }
        }

        if (!moveCard) {
            const card = this.game.wastePile?.lastCard;
            if (card) {
                this.game.foundations.forEach((f) => {
                    const lastFoundationCard = f.lastCard;
                    if (lastFoundationCard) {
                        if (lastFoundationCard.canDropInFoundation(card)) {
                            moveCard = card;
                        }
                    } else {
                        if (card.value === 1) moveCard = card;
                    }
                });
            }
        }
        return moveCard;
    }
    moveCard(card: Card) {
        card.triggerClick();
        // this.game.moves.add([card]);
    }

    getNextValue(game: SolitaireGame) {
        const valueList = game.foundations.map((f) => f.length);
        valueList.sort((a, b) => {
            if (a > b) return 1;
            if (a < b) return -1;
            return 0;
        });
        return valueList[0] + 1;
    }

    isAvailable() {
        let autoCompleteActive = true;
        if (this.game.gameOver) autoCompleteActive = false;
        if (this.game.stockPile.length) autoCompleteActive = false;
        if (Store.tutorialActive) autoCompleteActive = false;

        if (autoCompleteActive) {
            if (Store.isKlondike) autoCompleteActive = this.isAutocompleteKlondikeAvailable();
            if (Store.isSpider) autoCompleteActive = this.isAutocompleteSpiderAvailable();
            if (Store.isFreecell) autoCompleteActive = this.isAutocompleteFreeCellAvailable();
            if (Store.isPyramid) autoCompleteActive = false;
        }
        this.autocompleteButton.changeVisibility(autoCompleteActive);
    }
    isAutocompleteFreeCellAvailable(): boolean {
        let autoCompleteActive = true;
        if (autoCompleteActive) {
            for (let index = 0; index < this.game.columns.length; index++) {
                const column = this.game.columns[index];

                if (!column.length) continue;
                const inactiveCard = !column.cards[0].active;
                if (inactiveCard) {
                    autoCompleteActive = false;
                    break;
                }
            }
        }

        return autoCompleteActive;
    }
    isAutocompleteKlondikeAvailable(): boolean {
        let autoCompleteActive = true;

        if (autoCompleteActive) {
            for (let index = 0; index < this.game.columns.length; index++) {
                const column = this.game.columns[index];
                if (!column.length) continue;
                const hiddenCard = !column.cards[0].visible;
                if (hiddenCard) {
                    autoCompleteActive = false;
                    break;
                }
            }
        }

        if (autoCompleteActive) {
            const cardsInFoundation = [] as Card[][];
            const cardsInColumns = [] as Card[][];
            const cardsLefts = this.game.wastePile ? [...this.game.wastePile.cards] : [];

            this.game.foundations.forEach((foundation) => {
                const fList = [];
                const lastCard = foundation.lastCard;
                if (lastCard) fList.push(lastCard);
                cardsInFoundation.push(fList);
            });
            this.game.columns.forEach((column) => {
                const col = [] as Card[];
                column.cards.forEach((c) => {
                    col.push(c);
                });
                cardsInColumns.push(col);
            });
            while (autoCompleteActive && cardsLefts.length) {
                const checkCard = cardsLefts.pop();

                if (!checkCard) break;

                this.removeCardsFromColumns(cardsInColumns, cardsInFoundation);

                const foundationIndex = this.canGoToFoundation(checkCard, cardsInFoundation);
                if (foundationIndex > -1) {
                    cardsInFoundation[foundationIndex].push(checkCard);
                } else {
                    autoCompleteActive = false;
                    break;
                }
            }
        }
        return autoCompleteActive;
    }
    isAutocompleteSpiderAvailable(): boolean {
        let autoCompleteActive = true;
        if (autoCompleteActive) {
            for (let index = 0; index < this.game.columns.length; index++) {
                const column = this.game.columns[index];

                if (!column.length) continue;
                const inactiveCard = !column.cards[0].active;
                if (inactiveCard) {
                    autoCompleteActive = false;
                    break;
                }
            }
        }

        return autoCompleteActive;
    }
    removeCardsFromColumns(cardsInColumns: Card[][], cardsInFoundation: Card[][]) {
        let moveMade = true;
        while (moveMade) {
            moveMade = false;
            cardsInColumns.forEach((col) => {
                const lastCard = getLastItem(col);
                if (lastCard) {
                    const foundationIndex = this.canGoToFoundation(lastCard, cardsInFoundation);
                    if (foundationIndex > -1) {
                        const c = col.pop();
                        if (c) {
                            cardsInFoundation[foundationIndex].push(c);
                            moveMade = true;
                        }
                    }
                }
            });
        }
    }
    reset() {
        this.autocompleteButton.reset();
    }
    canGoToFoundation(card: Card, foundation: Card[][]) {
        if (!card) return -1;
        if (card.value === 1) return foundation.findIndex((f) => f.length === 0);
        for (let index = 0; index < foundation.length; index++) {
            const f = foundation[index];
            const lastCard = getLastItem(f);
            if (lastCard && lastCard.canDropInFoundation(card)) {
                return index;
            }
        }
        return -1;
    }
}
